import React from 'react';
import {
   Box,
   Typography,
   FormControlLabel,
   Checkbox
} from '@mui/material';
import styles from './admin.module.css';

/**
 * Reusable Permissions Section Component
 * Displays permission checkboxes organized in a grid layout
 * 
 * @param {Object} props - Component props
 * @param {Array} props.permission - Main permissions array
 * @param {Function} props.onPermissionChange - Handler for main permission changes
 * @param {Array} props.dashboardPermission - Dashboard permissions array
 * @param {Function} props.onDashboardPermissionChange - Handler for dashboard permission changes
 * @param {Array} props.sendgridPermission - Sendgrid permissions array
 * @param {Function} props.onSendgridPermissionChange - Handler for sendgrid permission changes
 * @param {Array} props.mediaRelationsPermission - Media relations permissions array
 * @param {Function} props.onMediaRelationsPermissionChange - Handler for media relations permission changes
 * @param {Array} props.companiesPermission - Companies permissions array
 * @param {Function} props.onCompaniesPermissionChange - Handler for companies permission changes
 * @param {Array} props.twitterXPermission - Twitter X permissions array
 * @param {Function} props.onTwitterXPermissionChange - Handler for twitter X permission changes
 * @param {Array} props.toolsPermission - Tools permissions array
 * @param {Function} props.onToolsPermissionChange - Handler for tools permission changes
 * @param {boolean} props.disabled - Whether the checkboxes should be disabled
 * @param {number} props.opacity - Opacity value for disabled state (default: 0.7)
 */
const PermissionsSection = ({
   permission = [],
   onPermissionChange,
   dashboardPermission = [],
   onDashboardPermissionChange,
   sendgridPermission = [],
   onSendgridPermissionChange,
   mediaRelationsPermission = [],
   onMediaRelationsPermissionChange,
   companiesPermission = [],
   onCompaniesPermissionChange,
   twitterXPermission = [],
   onTwitterXPermissionChange,
   entitlementPermission = [],
   onEntitlementPermissionChange,
   intercomPermission = [],
   onIntercomPermissionChange,
   toolsPermission = [],
   onToolsPermissionChange,
   auth0Permission = [],
   onAuth0PermissionChange,
   scriptRunnerv2Permission = [],
   onScriptRunnerv2PermissionChange,
   disabled = false,
   opacity = 0.7
}) => {

   const renderPermissionSection = (title, permissionList, changeHandler, height = '80px') => (
      <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
         <Typography variant="subtitle2" className={styles.sectionTitle}>
            {title}
         </Typography>
         <div
            className={styles.compactPermissionContainer}
            style={{
               height,
               opacity: disabled ? opacity : 1,
               pointerEvents: disabled ? 'none' : 'auto'
            }}
         >
            {['read', 'write', 'admin'].map((label, i) => (
               <FormControlLabel
                  key={i}
                  className={styles.compactCheckbox}
                  disabled={disabled}
                  control={
                     <Checkbox
                        value={label}
                        checked={permissionList.indexOf(label) !== -1}
                        onChange={changeHandler}
                        color="primary"
                        size="small"
                     />
                  }
                  label={label}
                  sx={{ opacity: disabled ? opacity : 1 }}
               />
            ))}
         </div>
      </Box>
   );

   return (
      <>
         {/* Main Permissions */}
         <Typography variant="subtitle2" className={styles.sectionTitle} sx={{ mt: 1 }}>
            Main Permissions
         </Typography>
         <div
            className={styles.compactPermissionContainer}
            style={{
               opacity: disabled ? opacity : 1,
               pointerEvents: disabled ? 'none' : 'auto'
            }}
         >
            {['jira', 'read', 'write', 'admin'].map((label, i) => (
               <FormControlLabel
                  key={i}
                  className={styles.compactCheckbox}
                  disabled={disabled}
                  control={
                     <Checkbox
                        value={label}
                        checked={permission.indexOf(label) !== -1}
                        onChange={onPermissionChange}
                        color="primary"
                        size="small"
                     />
                  }
                  label={label}
                  sx={{ opacity: disabled ? opacity : 1 }}
               />
            ))}
         </div>

         {/* Other permissions in 3x2 grid */}
         <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mt: 1 }}>
            {renderPermissionSection('Dashboard', dashboardPermission, onDashboardPermissionChange)}
            {renderPermissionSection('Sendgrid', sendgridPermission, onSendgridPermissionChange)}
            {renderPermissionSection('Media Relations', mediaRelationsPermission, onMediaRelationsPermissionChange)}
            {renderPermissionSection('Companies', companiesPermission, onCompaniesPermissionChange)}
            {renderPermissionSection('Twitter X', twitterXPermission, onTwitterXPermissionChange)}
            {renderPermissionSection('Entitlements', entitlementPermission, onEntitlementPermissionChange)}
            {renderPermissionSection('Intercom', intercomPermission, onIntercomPermissionChange)}
            {renderPermissionSection('Tools', toolsPermission, onToolsPermissionChange)}
            {renderPermissionSection('Auth0', auth0Permission, onAuth0PermissionChange)}
            {renderPermissionSection('Script Runner V2', scriptRunnerv2Permission, onScriptRunnerv2PermissionChange)}
         </Box>
      </>
   );
};

export default PermissionsSection;
